import { Component, ContentChild, DestroyRef, inject, OnInit, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import { InstanceUpdatesManagerService } from '@cloud-twin-v2/cloud-twin-shared/workers/instance-updates-manager.service';
import { ResultsPublishedWorkerResponse } from '@cloud-twin-v2/cloud-twin-shared/workers/instance-updates-worker-response';
import { CtNavigationFilterService } from '@cloud-twin-v2/navigation/services/ct-navigation-filter.service';
import { StartupService } from '@cloud-twin-v2/startup.service';
import { HttpCacheService } from '@common-modules/cache/http-cache/http-cache.service';
import { WlmNavigationService } from '@common-modules/navigation/wlm-navigation.service';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { WLMDialogResult } from '@common-modules/shared/model/dialog/wlm-dialog-result';
import { forkJoin, of, switchMap } from 'rxjs';

const COMPONENT_SELECTOR = 'ct-main-layout';

@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './ct-main-layout.component.html',
  styleUrl: './ct-main-layout.component.scss',
  standalone: false,
})
export class CtMainLayoutComponent implements OnInit {
  @ContentChild('card', { static: false }) cardTemplateRef: TemplateRef<any>;

  configLoaded = false;
  selectedWorkspaceName = 'Workspace 1';
  selectedWorkspaceDescription = 'Description 1';

  readonly T_SCOPE = `${TranslateScopes.MainLayout}.${COMPONENT_SELECTOR}`;

  private readonly _startupService = inject(StartupService);
  private readonly _selectedWorkspaceService = inject(SelectedWorkspaceService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _instanceUpdatesManagerService = inject(InstanceUpdatesManagerService);
  private readonly _dialogService = inject(DialogService);
  private readonly _cacheService = inject(HttpCacheService);
  private readonly _navigationService = inject(WlmNavigationService);
  private readonly _ctNavigationFilterService = inject(CtNavigationFilterService);

  constructor() {
    this._startupService.startup();

    this._startupService.configLoaded$.subscribe((configLoaded) => {
      this.configLoaded = configLoaded;
    });

    this.applyNavigationFilter();
  }

  ngOnInit(): void {
    this._selectedWorkspaceService.selectedWorkspace$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((workspace) => {
        this.selectedWorkspaceName = workspace?.workspaceName;
        this.selectedWorkspaceDescription = workspace?.workspaceDescription;
      });

    this._instanceUpdatesManagerService.resultsPublished$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((response) => this.handleResultsPublished(response));
  }

  private handleResultsPublished(resultsPublished: ResultsPublishedWorkerResponse): void {
    if (resultsPublished.refreshRequired) {
      this.handleMandatoryRefresh('common.reload.workspace.mandatory');
      return;
    }

    this.handleOptionalRefresh('common.reload.workspace.optional');
  }

  private handleMandatoryRefresh(message: string) {
    this._dialogService
      .showTranslatedDialogMessage({
        translateKey: message,
        icon: 'warning',
        showDenyButton: false,
      })
      .pipe(this.tryClearCaches())
      .subscribe({
        next: () => window.location.reload(),
        error: () => {},
      });
  }

  private handleOptionalRefresh(message: string) {
    this._dialogService
      .showTranslatedDialogMessage({
        translateKey: message,
        icon: 'warning',
      })
      .pipe(this.tryClearCaches())
      .subscribe({
        next: (response) => {
          if (!response) {
            return;
          }
          window.location.reload();
        },
        error: () => {},
      });
  }

  private tryClearCaches() {
    return switchMap((dialogResult: WLMDialogResult) => {
      if (!dialogResult.result) {
        return of(null);
      }

      return forkJoin([
        this._cacheService.clearContainsInUrl('result'),
        this._cacheService.clearContainsInUrl('attribute/metadata'),
        this._cacheService.clearContainsInUrl('attribute/date-range'),
      ]);
    });
  }

  private applyNavigationFilter(): void {
    const originalGetNavigationModules = this._navigationService.getNavigationModules.bind(
      this._navigationService
    );

    this._navigationService.getNavigationModules = () => {
      const modules$ = originalGetNavigationModules();
      return this._ctNavigationFilterService.filterNavigationModules(modules$);
    };
  }
}
